// Real zoomable outbreak map for Pathogen Watch — plotted from the LIVE
// surveillance signals (listSignals()) on a dark CARTO basemap.
//
// Markers (Claude Design review, MOVE 02): COLOUR = threat tier by default
// (catalog-derived, see threatTier.ts) or pathogen IDENTITY as a toggle; SHAPE =
// pathogen family (circle respiratory · triangle haemorrhagic · diamond vector ·
// square enteric · hexagon zoonotic) — a redundant, colour-blind-safe identity
// channel. Fill/stroke still encode confirmed vs unverified vs stale vs genomic.
//
// E2.3 per-capita view + staleness demotion + the non-situational-metric skip are
// preserved. Leaflet is a CDN global (index.html), pinned.

import { useEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { listSignals, SOURCE_CADENCE_MS, canonicalCountry } from '@biomax/plugin-surveillance-feed';
import { perCapitaFor, fmtPer100k } from './percapita';
import { signalThreatTier, THREAT_TIER_META, type ThreatTier } from './threatTier';

// Leaflet global (from the index.html script tag).
declare const L: any;

/** Count metrics where a per-capita (per-100k) view is meaningful. */
const COUNT_METRICS = new Set(['cases', 'deaths', 'hospitalisations']);

/** Metrics NOT plotted as situational outbreak dots (kept in the registry for
 *  source health + future indicator panels): lineage composition + intensity
 *  rates that are not case incidence. */
const NON_SITUATIONAL_METRICS = new Set([
  'seq-frequency', 'test-positivity', 'syndromic-rate', 'hospitalisation-rate',
]);

export type CountMode = 'per100k' | 'absolute';
export type ColorMode = 'threat' | 'identity';

const PATHOGEN_COLOR: Record<string, string> = {
  'ebola': '#ef4444', 'marburg': '#ef4444', 'nipah': '#fb923c', 'lassa': '#fb923c',
  'sars-cov-2': '#f472b6', 'influenza': '#a78bfa', 'measles': '#38bdf8', 'rsv': '#38bdf8',
  'mpxv': '#fbbf24', 'mpox': '#fbbf24', 'cholera': '#2dd4bf', 'dengue': '#2dd4bf',
  'west-nile': '#a3e635', 'cchf': '#fb7185',
};
export function colorFor(pathogenId: string): string {
  for (const key of Object.keys(PATHOGEN_COLOR)) if (pathogenId.includes(key)) return PATHOGEN_COLOR[key]!;
  return '#94a3b8'; // unverified-novel / unknown
}

/** Coarse pathogen family → marker SHAPE channel (first substring match wins;
 *  default respiratory). A 5-class grouping, never per-pathogen — shape can't
 *  scale to 40 identities, so the specific pathogen lives on the label/popup. */
export type PathogenFamily = 'respiratory' | 'haemorrhagic' | 'vector' | 'enteric' | 'zoonotic';
const FAMILY_RULES: [string, PathogenFamily][] = [
  ['ebola', 'haemorrhagic'], ['marburg', 'haemorrhagic'], ['lassa', 'haemorrhagic'],
  ['cchf', 'haemorrhagic'], ['rift-valley', 'haemorrhagic'], ['hantavirus', 'haemorrhagic'],
  ['dengue', 'vector'], ['zika', 'vector'], ['chikungunya', 'vector'], ['yellow-fever', 'vector'],
  ['west-nile', 'vector'], ['malaria', 'vector'], ['japanese', 'vector'],
  ['cholera', 'enteric'], ['typhoid', 'enteric'], ['hepatitis-a', 'enteric'],
  ['hepatitis-e', 'enteric'], ['norovirus', 'enteric'], ['polio', 'enteric'],
  ['nipah', 'zoonotic'], ['mpxv', 'zoonotic'], ['mpox', 'zoonotic'], ['plague', 'zoonotic'],
  ['anthrax', 'zoonotic'], ['tularemia', 'zoonotic'],
];
export const FAMILY_LABEL: Record<PathogenFamily, string> = {
  respiratory: 'Respiratory', haemorrhagic: 'Viral haemorrhagic',
  vector: 'Vector-borne', enteric: 'Enteric', zoonotic: 'Zoonotic / other',
};
export function familyFor(pathogenId: string): PathogenFamily {
  for (const [k, f] of FAMILY_RULES) if (pathogenId.includes(k)) return f;
  return 'respiratory';
}
function shapeSvg(family: PathogenFamily): string {
  switch (family) {
    case 'haemorrhagic': return '<polygon points="12,3 21,19 3,19"/>';
    case 'vector':       return '<polygon points="12,2 22,12 12,22 2,12"/>';
    case 'enteric':      return '<rect x="4" y="4" width="16" height="16" rx="2"/>';
    case 'zoonotic':     return '<polygon points="12,2 20,7 20,17 12,22 4,17 4,7"/>';
    default:             return '<circle cx="12" cy="12" r="9"/>';
  }
}

/** Per-signal staleness: stale when older than 2× its freshest source's cadence. */
const DEFAULT_CADENCE_MS = 24 * 3_600_000;
function staleness(s: { sources?: string[]; asOf: string }): { stale: boolean; ageDays: number } {
  const srcs = s.sources && s.sources.length ? s.sources : [];
  const cadence = srcs.length
    ? Math.min(...srcs.map(src => (SOURCE_CADENCE_MS as Record<string, number>)[src] ?? DEFAULT_CADENCE_MS))
    : DEFAULT_CADENCE_MS;
  const ageMs = Date.now() - Date.parse(s.asOf);
  return { stale: ageMs > 2 * cadence, ageDays: Math.floor(ageMs / 86_400_000) };
}

/** Stable key identifying a signal's marker, shared by the map + click-to-zoom. */
type SignalLike = { pathogenId: string; location: { lat: number; lon: number }; asOf: string };
const sigKey = (s: SignalLike): string => `${s.pathogenId}@${s.location.lat},${s.location.lon}@${s.asOf}`;

export interface OutbreakMapHandle { focus(sig: SignalLike): void }

/** Worst (highest-rank) threat tier in a group — drives cluster colour. */
function worstTierIn<T extends { tier: ThreatTier }>(arr: T[]): ThreatTier {
  return arr.reduce<ThreatTier>(
    (w, p) => THREAT_TIER_META[p.tier].rank > THREAT_TIER_META[w].rank ? p.tier : w,
    arr[0]!.tier,
  );
}

/** Tier-segmented donut glyph for clusters (R1) — replaces the old "solid worst-
 *  tier ring + count". Ring shows the tier MIX as a stacked-segment donut so the
 *  composition is legible at-a-glance; the centre count is coloured by the worst
 *  tier inside (the call-to-action). Inline SVG; theme-token coloured. */
function donutHtml<T extends { tier: ThreatTier }>(arr: T[], label?: string): string {
  const order: ThreatTier[] = ['critical', 'high', 'elevated', 'watch', 'routine'];
  const counts = order.map(t => ({ t, n: arr.filter(p => p.tier === t).length })).filter(d => d.n > 0);
  const total = arr.length;
  const R = 15, C = 2 * Math.PI * R, gap = 1.5;
  let off = 0;
  const segs = counts.map(d => {
    const frac = d.n / total;
    const len = Math.max(0, frac * C - gap);
    const seg = `<circle cx="17" cy="17" r="${R}" fill="none" stroke="${THREAT_TIER_META[d.t].color}" stroke-width="4" stroke-dasharray="${len} ${C - len}" stroke-dashoffset="${-off}" transform="rotate(-90 17 17)"/>`;
    off += frac * C;
    return seg;
  }).join('');
  const worst = worstTierIn(arr);
  return `<div class="pw-cluster__donut"><svg width="34" height="34" viewBox="0 0 34 34">`
    + `<circle cx="17" cy="17" r="${R}" fill="rgba(8,12,20,.92)" stroke="var(--bm-border)" stroke-width="4"/>`
    + segs
    + `<text x="17" y="21" text-anchor="middle" font-family="var(--bm-font-mono)" font-size="12" font-weight="700" fill="${THREAT_TIER_META[worst].color}">${label ?? total}</text>`
    + `</svg></div>`;
}

/** Larger country-rollup donut variant (R2). Visually distinguished from the
 *  co-located cluster donut (R3 CD-followup) by: (a) larger size (48 vs 34),
 *  (b) an OUTER accent ring + dashed identity ring around the tier segments,
 *  (c) ISO3 country code in the centre instead of a bare count, (d) the count
 *  rendered below the ISO3 in muted ink. Click behaviour differs: country
 *  donut = zoom in (popup offers cross-surface link); cluster donut =
 *  spiderfy / zoom (no cross-surface jump). */
function countryDonutHtml<T extends { tier: ThreatTier }>(arr: T[], iso3: string): string {
  const order: ThreatTier[] = ['critical', 'high', 'elevated', 'watch', 'routine'];
  const counts = order.map(t => ({ t, n: arr.filter(p => p.tier === t).length })).filter(d => d.n > 0);
  const total = arr.length;
  const R = 18, C = 2 * Math.PI * R, gap = 2;
  let off = 0;
  const segs = counts.map(d => {
    const frac = d.n / total;
    const len = Math.max(0, frac * C - gap);
    const seg = `<circle cx="24" cy="24" r="${R}" fill="none" stroke="${THREAT_TIER_META[d.t].color}" stroke-width="5" stroke-dasharray="${len} ${C - len}" stroke-dashoffset="${-off}" transform="rotate(-90 24 24)"/>`;
    off += frac * C;
    return seg;
  }).join('');
  const worst = worstTierIn(arr);
  return `<div class="pw-cluster__donut pw-cluster__donut--country" title="${iso3} — ${total} sub-national signals · click to zoom in or open Country Profile">`
    + `<svg width="48" height="48" viewBox="0 0 48 48">`
      // Outer dashed identity ring — visually marks this as a country rollup,
      // not a co-located cluster. Accent-coloured to read as a "destination".
      + `<circle cx="24" cy="24" r="22" fill="none" stroke="var(--bm-accent)" stroke-width="1" stroke-dasharray="3 3" opacity="0.65"/>`
      // Solid inner background + the tier segments.
      + `<circle cx="24" cy="24" r="${R}" fill="rgba(8,12,20,.94)" stroke="var(--bm-border)" stroke-width="5"/>`
      + segs
      + `<text x="24" y="22" text-anchor="middle" font-family="var(--bm-font-mono)" font-size="10" font-weight="700" fill="${THREAT_TIER_META[worst].color}">${iso3}</text>`
      + `<text x="24" y="34" text-anchor="middle" font-family="var(--bm-font-mono)" font-size="11" font-weight="700" fill="var(--bm-text)">${total}</text>`
    + `</svg></div>`;
}

/** Scaled spiderfy layout (R1) — replaces the old fixed-radius circle.
 *  - n ≤ 8: scaled circle (radius grows with n so 30px markers never collide).
 *  - n  > 8: Archimedean spiral (r = a + b·θ) with constant arc-length step so
 *    consecutive markers stay equidistant even far out from the centroid.
 *  Returns layer-point coords; caller converts back to latlng. */
function spiralPoints(n: number, cx: number, cy: number): Array<[number, number]> {
  const pts: Array<[number, number]> = [];
  if (n <= 8) {
    const R = Math.max(34, (n * 30) / (2 * Math.PI));      // P4: bumped from 32 → 34 for cleaner 26px-marker separation
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 - Math.PI / 2;
      pts.push([cx + Math.cos(a) * R, cy + Math.sin(a) * R]);
    }
  } else {
    let theta = 0;
    const step = 34, b = 9;                                // P4: step 30 → 34 for cleaner arc-length separation
    for (let i = 0; i < n; i++) {
      const r = 26 + b * theta;
      pts.push([cx + Math.cos(theta) * r, cy + Math.sin(theta) * r]);
      theta += step / r;          // constant arc length
    }
  }
  return pts;
}

/** Below this zoom, sub-national signals from a country with >= ROLLUP_MIN
 *  signals collapse to a single country donut at the canonical centroid.
 *  Prevents the "density looks like threat" failure when 8 AU states / 11 UK
 *  regions / 4 SA pathogens etc. blanket their host country with markers. */
const ROLLUP_ZOOM_THRESHOLD = 4;
const ROLLUP_MIN_SIGNALS = 3;

/** OS-level reduced-motion preference (L3). Vestibular-trigger gating for
 *  map pan/zoom + fly-to. Called per-event so a user toggling the OS setting
 *  takes effect on the next interaction without remounting the map. */
function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined'
    && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

/** Resolve a signal's `geoResolution` field, falling back to inference when
 *  the adapter hasn't set it explicitly (transitional scaffold — every
 *  adapter is being backfilled, but inference keeps the map honest in the
 *  meantime). Heuristic (CD-aligned):
 *    no iso3                             → point (free-coordinate event)
 *    iso3 + admin matches country name   → national (aggregate)
 *    iso3 + admin differs from country   → area (sub-national)
 *  Emits a one-shot console warning per source-id the first time inference
 *  fires there, so the dev burn-down is visible without spamming. */
const inferenceWarnedSources = new Set<string>();
function resolveGeoResolution(
  s: { geoResolution?: 'point' | 'area' | 'national' | 'nonspatial' | 'unknown';
       location: { admin: string; iso3?: string };
       source: string },
): 'point' | 'area' | 'national' | 'nonspatial' | 'unknown' {
  if (s.geoResolution) return s.geoResolution;
  if (!inferenceWarnedSources.has(s.source)) {
    inferenceWarnedSources.add(s.source);
    // eslint-disable-next-line no-console
    console.warn(`[OutbreakMap] geoResolution missing on signals from "${s.source}" — inferring (transitional scaffold). Adapter should set the field explicitly.`);
  }
  const iso3 = s.location.iso3;
  if (!iso3) return 'point';
  const cc = canonicalCountry(iso3);
  if (cc && s.location.admin && s.location.admin.toLowerCase() === cc.name.toLowerCase()) return 'national';
  return 'area';
}

/** The SVG divIcon for one signal, coloured by the active mode + family-shaped. */
function markerHtml(
  s: ReturnType<typeof listSignals>[number],
  colorMode: ColorMode,
  tier: ThreatTier,
  stale: boolean,
): string {
  const isGenomic = s.metric === 'sequences';
  const confirmed = s.verificationStatus === 'confirmed';
  const color = colorMode === 'identity' ? colorFor(s.pathogenId) : THREAT_TIER_META[tier].color;
  let style: string;
  if (isGenomic) style = `fill:none;stroke:${color};stroke-width:1.6;stroke-dasharray:2 2;opacity:.85`;
  else if (stale) style = `fill:${color};fill-opacity:.16;stroke:${color};stroke-width:1.2;stroke-dasharray:2 3;opacity:.7`;
  else if (confirmed) style = `fill:${color};fill-opacity:.9;stroke:#fff;stroke-width:1.3`;
  else style = `fill:${color};fill-opacity:.2;stroke:${color};stroke-width:1.6;stroke-dasharray:2 2`;
  const sz = confirmed && !isGenomic ? 26 : 22;
  return `<svg width="${sz}" height="${sz}" viewBox="0 0 24 24" style="overflow:visible;filter:drop-shadow(0 1px 2px rgba(0,0,0,.6))">`
    + `<g style="${style}">${shapeSvg(familyFor(s.pathogenId))}</g></svg>`;
}

/** Build the count line(s) of a marker popup for the active mode. */
function countLines(s: ReturnType<typeof listSignals>[number], mode: CountMode): string {
  if (s.metric === 'event-notification') {
    return `<div style="font-size:12px">Official outbreak event reported</div>` +
      (s.context?.suspectedCases != null ? `<div style="font-size:12px">suspected: <b>${s.context.suspectedCases.toLocaleString()}</b></div>` : '') +
      (s.context?.deaths != null ? `<div style="font-size:12px">deaths: <b>${s.context.deaths.toLocaleString()}</b></div>` : '') +
      `<div style="font-size:11px;color:var(--bm-text-subtle);margin-top:2px">confirmed count not in this report — see source</div>`;
  }
  if (s.metric === 'sequences') {
    return `<div style="font-size:12px">genomic detections: <b>${s.value.toLocaleString()}</b> sequence(s)</div>` +
      `<div style="font-size:11px;color:var(--bm-prov-synthetic);margin-top:2px">genomic sampling effort — NOT a case count</div>`;
  }
  const isCount = COUNT_METRICS.has(s.metric);
  const label = s.metric === 'cases' && s.sources.includes('who-don') ? 'confirmed' : s.metric;
  if (isCount && mode === 'per100k') {
    const pc = perCapitaFor(s);
    if (pc) {
      return `<div style="font-size:12px">${label}: <b>${fmtPer100k(pc.rate)}</b> /100k</div>` +
        `<div style="font-size:11px;color:var(--bm-text-subtle)">${s.value.toLocaleString()} absolute · pop ${(pc.population / 1e6).toFixed(1)}M (${pc.matchedBy})</div>`;
    }
    return `<div style="font-size:12px">${label}: <b>${s.value.toLocaleString()}</b></div>` +
      `<div style="font-size:11px;color:var(--bm-prov-stale)">no population denominator — absolute count</div>`;
  }
  return `<div style="font-size:12px">${label}: <b>${s.value.toLocaleString()}</b></div>` +
    (s.metric === 'cases' && s.sources.includes('who-don')
      ? (s.context?.suspectedCases != null ? `<div style="font-size:12px;color:var(--bm-text-subtle)">suspected: ${s.context.suspectedCases.toLocaleString()}</div>` : '') +
        (s.context?.deaths != null ? `<div style="font-size:12px;color:var(--bm-text-subtle)">deaths: ${s.context.deaths.toLocaleString()}</div>` : '')
      : '');
}

/** Full popup — value + threat tier + auditable threat basis + provenance, all preserved. */
function popupHtml(s: ReturnType<typeof listSignals>[number], mode: CountMode): string {
  const { stale, ageDays } = staleness(s);
  const tr = signalThreatTier(s, { stale });
  const tm = THREAT_TIER_META[tr.tier];
  const idColor = colorFor(s.pathogenId);
  const conflicts = s.context?.conflictingReports;

  let basis: string;
  if (tr.reason === 'genomic') basis = 'genomic sampling — context layer';
  else if (tr.reason === 'stale') basis = 'demoted — stale data';
  else if (tr.reason === 'unknown') basis = 'severity unknown — pathogen not in catalog';
  else {
    const pct = (tr.ifr ?? 0) * 100;
    basis = `IFR ${pct >= 1 ? pct.toFixed(0) : pct.toFixed(1)}% · R0 up to ${tr.r0}`;
    if (tr.cappedByVerification) basis += ' · capped: unverified';
  }

  return `<div style="min-width:210px">`
    + `<div style="display:flex;align-items:center;gap:7px;margin-bottom:3px">`
    + `<span style="font-weight:700;color:${idColor}">${s.pathogenId}${s.subtype ? ' · ' + s.subtype : ''}</span>`
    + `<span style="margin-left:auto;font:700 10px var(--bm-font-mono);color:${tm.color};border:1px solid ${tm.color};border-radius:999px;padding:1px 7px">${tm.label}</span></div>`
    + `<div style="color:var(--bm-text-subtle);font-size:11.5px;margin:0 0 6px">${s.location.admin} · ${FAMILY_LABEL[familyFor(s.pathogenId)]}</div>`
    + countLines(s, mode)
    + (conflicts && conflicts.length
        ? `<div style="font-size:11px;color:var(--bm-prov-conflict);margin-top:3px">⚠ sources disagree: ${s.value.toLocaleString()} vs ${conflicts.map(c => c.value.toLocaleString()).join(' / ')}</div>`
        : '')
    + (stale ? `<div style="font-size:11px;color:var(--bm-prov-stale);margin-top:3px">⏳ STALE — ${ageDays}d old, source cadence exceeded; reference only, not current</div>` : '')
    + `<div style="font-size:11px;color:var(--bm-text-muted);margin-top:6px;border-top:1px solid var(--bm-border);padding-top:5px"><span style="color:var(--bm-text-subtle)">threat basis ·</span> ${basis}</div>`
    + `<div style="font-size:11px;color:var(--bm-text-subtle);margin-top:5px">as of ${s.asOf.slice(0, 10)} · ${s.verificationStatus.toUpperCase()}</div>`
    + `<div style="font-size:11px;color:var(--bm-text-subtle);margin-top:2px">source: ${s.sources.join(', ')}</div></div>`;
}

export interface OutbreakMapProps {
  hiddenPathogens?: Set<string>;
  /** Called when the user clicks a country-rollup donut at world zoom. The
   *  intent is to take them to §7.18 Country Profile for the drilldown — the
   *  donut shows "this country has 8 sub-national signals" and CountryProfile
   *  answers "which sources, what pathogens, how fresh, per row". */
  onJumpToCountryProfile?: (iso3: string) => void;
}

export const OutbreakMap = forwardRef<OutbreakMapHandle, OutbreakMapProps>(
  function OutbreakMap({ hiddenPathogens, onJumpToCountryProfile }, fwdRef) {
  const ref = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const layerRef = useRef<any>(null);
  const markersRef = useRef<Map<string, any>>(new Map());
  const drawRef = useRef<() => void>(() => {});
  const [failed, setFailed] = useState(false);
  const [mode, setMode] = useState<CountMode>('per100k');
  const [colorMode, setColorMode] = useState<ColorMode>('threat');
  const [expanded, setExpanded] = useState<string | null>(null); // centroid key being spiderfied
  const [zoom, setZoom] = useState<number>(2);                   // track for country rollup
  const [rollup, setRollup] = useState<boolean>(true);

  useImperativeHandle(fwdRef, () => ({
    focus(sig) {
      const map = mapRef.current;
      if (!map) return;
      map.setView([sig.location.lat, sig.location.lon], 6, { animate: true });
      const m = markersRef.current.get(sigKey(sig));
      if (m) m.openPopup();
    },
  }), []);

  // P3 (CD followup): Esc collapses an open spider. Document keyboard parity:
  // tabbing markers across 600 divIcons is hostile; the triage band + table
  // are the documented keyboard equivalents (see HonestyCaption for the
  // visible note). This handler is the one explicit keyboard hook on the map.
  useEffect(() => {
    if (expanded === null) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setExpanded(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expanded]);

  // P2 (CD followup): if the user zooms back out below the rollup threshold
  // while a sub-national-country cluster is spiderfied, that cluster's group
  // gets ABSORBED into the country rollup and the spider silently vanishes
  // — but `expanded` stays set, so any future zoom-in that re-creates the
  // same group key would auto-spiderfy without an explicit user click.
  // Collapse on any zoom-driven transition into rollup territory.
  useEffect(() => {
    if (expanded !== null && rollup && zoom < ROLLUP_ZOOM_THRESHOLD) {
      setExpanded(null);
    }
  }, [zoom, rollup, expanded]);

  // Create the map once.
  useEffect(() => {
    if (!ref.current) return;
    if (typeof L === 'undefined') { setFailed(true); return; }
    const map = L.map(ref.current, {
      worldCopyJump: true, minZoom: 2, maxZoom: 10, zoomControl: true,
      // L3 (CD review): respect the OS-level reduced-motion preference. fly-to,
      // pan-animations and the spiderfy expansion are the vestibular-triggering
      // moves; the global CSS reduced-motion rule can't reach Leaflet's
      // internal pan/zoom, so we hand the preference to L.map() directly.
      zoomAnimation: !prefersReducedMotion(),
      fadeAnimation: !prefersReducedMotion(),
      markerZoomAnimation: !prefersReducedMotion(),
    })
      .setView([20, 10], 2);
    L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
      subdomains: 'abcd', maxZoom: 19, attribution: '© OpenStreetMap contributors © CARTO',
    }).addTo(map);
    mapRef.current = map;
    layerRef.current = L.layerGroup().addTo(map);
    // Re-anchor spider legs on zoom/move + keep React zoom state in sync for
    // the country-rollup threshold check.
    const onView = () => {
      setZoom(map.getZoom());
      drawRef.current();
    };
    map.on('zoomend moveend', onView);
    const t = setTimeout(() => map.invalidateSize(), 60);
    return () => {
      clearTimeout(t);
      map.off('zoomend moveend', onView);
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
  }, []);

  // Memoize tier + staleness derivation per-signal (CD P1 perf fix).
  // Per-redraw the only thing that should change on pan/zoom is layout — NOT
  // tier (catalog lookup + median IFR sort) or staleness (cadence comparison),
  // both of which are functions of the feed snapshot alone. Recomputing them
  // on every moveend across ~600 signals was burning frames on pan.
  // Re-derives only when listSignals identity changes or hiddenPathogens
  // changes (those are the only inputs to the filter+derive step).
  type GeoRes = 'point' | 'area' | 'national' | 'nonspatial' | 'unknown';
  type Plotted = { s: ReturnType<typeof listSignals>[number]; tier: ThreatTier; stale: boolean; geo: GeoRes };
  const plottable = useMemo<Plotted[]>(() => {
    const out: Plotted[] = [];
    for (const s of listSignals()) {
      if (NON_SITUATIONAL_METRICS.has(s.metric)) continue;
      if (hiddenPathogens?.has(s.pathogenId)) continue;
      const geo = resolveGeoResolution(s);
      if (geo === 'nonspatial') continue;                          // CD geoResolution rule: table-only, never on the map
      const { stale } = staleness(s);
      const tier = signalThreatTier(s, { stale }).tier;
      out.push({ s, tier, stale, geo });
    }
    return out;
  }, [hiddenPathogens]);

  // (Re)draw markers when mode / colour / filter / spiderfy-state / zoom / rollup changes.
  // Co-located signals (England, USA, per-country centroids) would otherwise
  // stack so only the top one is clickable — a correctness bug, not just polish.
  // They cluster into a tier-donut bubble (coloured by the WORST tier inside,
  // ring shows the tier mix) and spiderfy on click (scaled spiral) so every
  // signal stays reachable with its provenance intact. Sub-national signals
  // from countries with ≥3 signals collapse to a country donut below zoom 4.
  useEffect(() => {
    drawRef.current = () => {
      const map = mapRef.current;
      const layer = layerRef.current;
      if (!map || !layer) return;
      layer.clearLayers();
      markersRef.current.clear();
      const all = plottable;

      // Country rollup (R2): below the zoom threshold, sub-national-dense countries
      // collapse to one donut at the canonical centroid; the rest plot normally.
      // CD geoResolution rule: ONLY `area` signals are rollup-eligible. `national`
      // aggregates are not absorbed (the donut would inflate by double-counting);
      // they get surfaced as a "national aggregate" header in the country popup.
      const z = map.getZoom();
      const doRollup = rollup && z < ROLLUP_ZOOM_THRESHOLD;
      const rolledUpIso = new Set<string>();
      if (doRollup) {
        const areasByIso = new Map<string, Plotted[]>();
        const nationalByIso = new Map<string, Plotted[]>();
        for (const p of all) {
          const iso = p.s.location.iso3;
          if (!iso) continue;
          if (p.geo === 'area') (areasByIso.get(iso) ?? areasByIso.set(iso, []).get(iso)!).push(p);
          else if (p.geo === 'national') (nationalByIso.get(iso) ?? nationalByIso.set(iso, []).get(iso)!).push(p);
        }
        for (const [iso, arr] of areasByIso) {
          if (arr.length < ROLLUP_MIN_SIGNALS) continue;
          const cc = canonicalCountry(iso);
          if (!cc) continue;
          rolledUpIso.add(iso);
          const nationalAggregates = nationalByIso.get(iso) ?? [];
          const icon = L.divIcon({
            className: 'pw-cluster', html: countryDonutHtml(arr, iso),
            iconSize: [48, 48], iconAnchor: [24, 24],
          });
          const worst = worstTierIn(arr);
          // CD R3-followup: click = zoom in one level (stay on map); the popup
          // carries an explicit "→ Country Profile" link so the cross-surface
          // navigation is opt-in, never a surprise yank. Distinct from
          // co-located cluster donuts (those spiderfy on click).
          const profileHref = `#/country-profile?iso3=${iso}`;
          // National-aggregate header (CD geoResolution rule): if the country
          // also has its own national-aggregate signals (not absorbed into
          // the sub-national rollup), surface them here so the operator sees
          // both layers without double-counting them in the donut's tier mix.
          const aggHeader = nationalAggregates.length > 0
            ? `<div style="font-size:11.5px;margin-bottom:6px;padding:5px 7px;border-radius:4px;background:var(--bm-bg-subtle);border-left:2px solid var(--bm-accent)">`
              + `<span style="font-family:var(--bm-font-mono);font-size:10px;color:var(--bm-text-subtle);text-transform:uppercase;letter-spacing:0.04em">National aggregate · separate from rollup</span><br/>`
              + nationalAggregates.map(p => `<span style="color:var(--bm-text)">${p.s.pathogenId}</span> <span style="color:var(--bm-text-muted)">${p.s.metric}</span> <b style="color:${THREAT_TIER_META[p.tier].color}">${(p.s.value as number).toLocaleString()}</b>`).join(' · ')
            + `</div>`
            : '';
          const popup = `<div style="min-width:240px">`
            + `<div style="display:flex;align-items:center;gap:7px;margin-bottom:3px">`
              + `<span style="font:700 11px var(--bm-font-mono);color:var(--bm-accent);background:var(--bm-accent-subtle);border-radius:999px;padding:2px 8px">${iso}</span>`
              + `<b>${cc.name}</b>`
              + `<span style="margin-left:auto;font:700 10px var(--bm-font-mono);color:${THREAT_TIER_META[worst].color};border:1px solid ${THREAT_TIER_META[worst].color};border-radius:999px;padding:1px 7px">${THREAT_TIER_META[worst].label}</span>`
            + `</div>`
            + `<div style="color:var(--bm-text-subtle);font-size:11.5px;margin:0 0 6px">${arr.length} sub-national signals · ROLLED UP at world zoom</div>`
            + `<div style="font-size:12px;margin-bottom:8px">tier mix: ${(['critical', 'high', 'elevated', 'watch', 'routine'] as ThreatTier[]).filter(t => arr.some(p => p.tier === t)).map(t => `<span style="color:${THREAT_TIER_META[t].color}">${arr.filter(p => p.tier === t).length} ${THREAT_TIER_META[t].label.toLowerCase()}</span>`).join(' · ')}</div>`
            + aggHeader
            + `<div style="display:flex;gap:6px;align-items:center;flex-wrap:wrap;font-size:11px">`
              + `<span style="color:var(--bm-text-subtle)">click donut to zoom in · or</span>`
              + `<a href="${profileHref}" style="color:var(--bm-accent);text-decoration:none;font-weight:600;border-bottom:1px dashed currentColor;padding-bottom:1px">→ §7.18 Country Profile</a>`
            + `</div></div>`;
          const marker = L.marker(cc.centroid, { icon })
            .bindPopup(popup)
            .on('click', () => {
              // Default click = zoom in one level (stay on map). Popup also
              // opens automatically and offers the cross-surface link.
              if (prefersReducedMotion()) map.setView(cc.centroid, ROLLUP_ZOOM_THRESHOLD + 1, { animate: false });
              else map.flyTo(cc.centroid, ROLLUP_ZOOM_THRESHOLD + 1, { duration: 0.5 });
            })
            .addTo(layer);
          // Wire the popup's "→ Country Profile" anchor to the React navigate
          // callback (preserves SPA hash routing instead of full-page jump).
          marker.on('popupopen', (ev: { popup: { _container: HTMLElement | null } }) => {
            const root = ev.popup._container;
            if (!root || !onJumpToCountryProfile) return;
            const link = root.querySelector('a[href^="#/country-profile"]') as HTMLAnchorElement | null;
            if (link) {
              link.onclick = (e) => { e.preventDefault(); onJumpToCountryProfile(iso); };
            }
          });
        }
      }

      // Anything not rolled up clusters by lat/lon as before.
      const groups = new Map<string, Plotted[]>();
      for (const p of all) {
        if (doRollup && p.s.location.iso3 && rolledUpIso.has(p.s.location.iso3)) continue;
        const key = `${p.s.location.lat.toFixed(2)},${p.s.location.lon.toFixed(2)}`;
        (groups.get(key) ?? groups.set(key, []).get(key)!).push(p);
      }

      const place = (p: Plotted, latlng: [number, number]) => {
        const icon = L.divIcon({
          className: 'pw-marker', html: markerHtml(p.s, colorMode, p.tier, p.stale),
          iconSize: [26, 26], iconAnchor: [13, 13],
        });
        const marker = L.marker(latlng, { icon }).bindPopup(popupHtml(p.s, mode)).addTo(layer);
        markersRef.current.set(sigKey(p.s), marker);
      };

      for (const [key, arr] of groups) {
        const c: [number, number] = [arr[0]!.s.location.lat, arr[0]!.s.location.lon];
        if (arr.length === 1) { place(arr[0]!, c); continue; }

        if (expanded === key) {
          // Spiral spiderfy (R1) — scaled radius / Archimedean spiral
          const center = map.latLngToLayerPoint(c);
          const pts = spiralPoints(arr.length, center.x, center.y);
          arr.forEach((p, i) => {
            const [x, y] = pts[i]!;
            const ll = map.layerPointToLatLng(L.point(x, y));
            L.polyline([c, [ll.lat, ll.lng]], { color: 'var(--bm-border-strong)', weight: 1, opacity: 0.5 }).addTo(layer);
            place(p, [ll.lat, ll.lng]);
          });
          // P3 (CD followup): for n ≤ 8 the markers all sit on a ring around the
          // centroid — the centre is EMPTY, so the collapse × can sit there. Only
          // the spiral case (n > 8, which fills the centre) needs the NW offset
          // to avoid landing on a leg marker.
          const center2 = map.latLngToLayerPoint(c);
          const collapseLatLng = arr.length <= 8
            ? c
            : (() => { const p = map.layerPointToLatLng(L.point(center2.x - 24, center2.y - 24)); return [p.lat, p.lng] as [number, number]; })();
          const collapse = L.divIcon({
            className: 'pw-cluster',
            html: `<div class="pw-cluster__node pw-cluster__collapse" style="color:var(--bm-text-subtle);border-color:var(--bm-border-strong)" title="Collapse (Esc)">×</div>`,
            iconSize: [22, 22], iconAnchor: [11, 11],
          });
          L.marker(collapseLatLng, { icon: collapse })
            .on('click', () => setExpanded(null))
            .addTo(layer);
        } else {
          // Tier-segmented donut (R1)
          const icon = L.divIcon({
            className: 'pw-cluster', html: donutHtml(arr),
            iconSize: [34, 34], iconAnchor: [17, 17],
          });
          L.marker(c, { icon })
            .on('click', () => {
              // Zoom-to-expand (R1): far-out click flies in first; only spiderfy
              // when zoom is close enough that legs won't blanket a continent.
              if (map.getZoom() < 4) {
                if (prefersReducedMotion()) map.setView(c, 5, { animate: false });
                else map.flyTo(c, 5, { duration: 0.5 });
              } else {
                setExpanded(key);
              }
            })
            .addTo(layer);
        }
      }
    };
    drawRef.current();
  }, [plottable, mode, colorMode, expanded, zoom, rollup, onJumpToCountryProfile]);

  const MAP_HEIGHT = 'clamp(520px, 72vh, 900px)';
  if (failed) {
    return (
      <div style={{ height: MAP_HEIGHT, display: 'grid', placeItems: 'center', background: '#06090f',
        color: 'var(--bm-text-muted)', borderRadius: 'var(--bm-radius-md)', fontSize: 13, textAlign: 'center', padding: 24 }}>
        Map requires connectivity (Leaflet + basemap tiles load from CDN).<br />Live signal data is still available in the table below.
      </div>
    );
  }
  return (
    <div style={{ position: 'relative', height: MAP_HEIGHT, borderRadius: 'var(--bm-radius-md)', overflow: 'hidden', background: '#06090f' }}>
      <div ref={ref} style={{ height: '100%', width: '100%' }} />
      <ColorModeToggle mode={colorMode} onChange={setColorMode} />
      <CountModeToggle mode={mode} onChange={setMode} />
      <RollupToggle on={rollup} onChange={setRollup} active={rollup && zoom < ROLLUP_ZOOM_THRESHOLD} />
      <HonestyCaption />
      <MapLegend />
    </div>
  );
});

const toggleWrap = (side: 'left' | 'right'): React.CSSProperties => ({
  position: 'absolute', top: 10, [side]: 10, zIndex: 1000,
  display: 'flex', borderRadius: 'var(--bm-radius-sm, 6px)', overflow: 'hidden',
  border: '1px solid rgba(148,163,184,0.35)', background: 'rgba(6,9,15,0.86)',
  backdropFilter: 'blur(4px)', boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
});
const toggleBtn: React.CSSProperties = {
  appearance: 'none', border: 'none', cursor: 'pointer',
  font: '600 11px/1 var(--bm-font-mono, ui-monospace, monospace)',
  letterSpacing: '0.02em', padding: '6px 10px', color: '#cbd5e1', background: 'transparent',
};
const toggleActive: React.CSSProperties = { ...toggleBtn, background: 'var(--bm-accent)', color: '#fff' };

/** Colour = threat tier / pathogen identity. */
function ColorModeToggle({ mode, onChange }: { mode: ColorMode; onChange: (m: ColorMode) => void }) {
  return (
    <div role="group" aria-label="Map marker colour" title="What marker colour encodes" style={toggleWrap('left')}>
      <button type="button" style={mode === 'threat' ? toggleActive : toggleBtn} aria-pressed={mode === 'threat'} onClick={() => onChange('threat')}>colour = threat</button>
      <button type="button" style={mode === 'identity' ? toggleActive : toggleBtn} aria-pressed={mode === 'identity'} onClick={() => onChange('identity')}>identity</button>
    </div>
  );
}

/** Per-100k / absolute toggle. */
function CountModeToggle({ mode, onChange }: { mode: CountMode; onChange: (m: CountMode) => void }) {
  return (
    <div role="group" aria-label="Map case-count display" title="How case counts are displayed on the map" style={toggleWrap('right')}>
      <button type="button" style={mode === 'per100k' ? toggleActive : toggleBtn} aria-pressed={mode === 'per100k'} onClick={() => onChange('per100k')}>per 100k</button>
      <button type="button" style={mode === 'absolute' ? toggleActive : toggleBtn} aria-pressed={mode === 'absolute'} onClick={() => onChange('absolute')}>absolute</button>
    </div>
  );
}

/** Country-rollup toggle (R2). Below zoom 4, countries with ≥3 sub-national
 *  signals collapse to a single country donut at the canonical centroid. */
function RollupToggle({ on, onChange, active }: { on: boolean; onChange: (v: boolean) => void; active: boolean }) {
  const wrap: React.CSSProperties = {
    position: 'absolute', top: 46, right: 10, zIndex: 1000,
    display: 'flex', borderRadius: 'var(--bm-radius-sm, 6px)', overflow: 'hidden',
    border: '1px solid rgba(148,163,184,0.35)', background: 'rgba(6,9,15,0.86)',
    backdropFilter: 'blur(4px)', boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
  };
  return (
    <div role="group" aria-label="Sub-national rollup" title="At world zoom, collapse sub-national signals (AU states, UK regions, etc.) to one country donut so density doesn't fake threat. Breaks out automatically on zoom-in." style={wrap}>
      <button type="button" style={on ? toggleActive : toggleBtn} aria-pressed={on} onClick={() => onChange(true)}>
        rollup {active ? '· on' : ''}
      </button>
      <button type="button" style={!on ? toggleActive : toggleBtn} aria-pressed={!on} onClick={() => onChange(false)}>raw</button>
    </div>
  );
}

/** Persistent honesty caption (L4 / R2 pairing). Reminds the operator that
 *  marker density reflects reporting granularity, not relative threat. Also
 *  carries the P5 a11y note: 600 markers are not keyboard-operable (would be
 *  hostile to tab through), so the triage band + signal tables are the
 *  documented keyboard path. Esc collapses an open spider. */
function HonestyCaption() {
  return (
    <div style={{
      position: 'absolute', bottom: 10, left: 10, zIndex: 999,
      maxWidth: 380,
      padding: '6px 10px', borderRadius: 'var(--bm-radius-sm, 6px)',
      background: 'rgba(6,9,15,0.78)', backdropFilter: 'blur(4px)',
      border: '1px solid var(--bm-border)',
      font: '11px/1.45 var(--bm-font-mono, ui-monospace, monospace)',
      color: 'var(--bm-text-muted)', pointerEvents: 'none',
    }}>
      <div>
        density of markers reflects <strong style={{ color: 'var(--bm-text)' }}>reporting granularity</strong>, not relative threat · colour carries threat
      </div>
      <div style={{ marginTop: 4, fontSize: 10, opacity: 0.8 }}>
        keyboard: use the triage band + tables below · Esc collapses a spider
      </div>
    </div>
  );
}

/** Always-visible map legend (L4) — colour / shape / state key + the
 *  "genomic ≠ case count" honesty line lifted out of popups. Pinned bottom-right. */
function MapLegend() {
  const [open, setOpen] = useState<boolean>(true);
  const wrap: React.CSSProperties = {
    position: 'absolute', bottom: 10, right: 10, zIndex: 999,
    minWidth: 180, maxWidth: 230,
    padding: open ? '8px 10px 10px' : '4px 10px',
    borderRadius: 'var(--bm-radius-sm, 6px)',
    background: 'rgba(6,9,15,0.86)', backdropFilter: 'blur(4px)',
    border: '1px solid var(--bm-border)',
    font: '11px/1.45 var(--bm-font-sans, system-ui)',
    color: 'var(--bm-text-muted)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
  };
  const head: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 6,
    fontFamily: 'var(--bm-font-mono)', fontSize: 10, letterSpacing: '0.04em',
    textTransform: 'uppercase', color: 'var(--bm-text-subtle)', cursor: 'pointer',
  };
  const sec: React.CSSProperties = {
    fontFamily: 'var(--bm-font-mono)', fontSize: 10, letterSpacing: '0.04em',
    textTransform: 'uppercase', color: 'var(--bm-text-subtle)', marginTop: 6, marginBottom: 3,
  };
  const row: React.CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: '4px 10px', marginTop: 1 };
  const item: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--bm-text)' };
  const dot = (c: string): React.CSSProperties => ({ width: 8, height: 8, borderRadius: '50%', background: c, display: 'inline-block' });
  const tiers: ThreatTier[] = ['critical', 'high', 'elevated', 'watch', 'routine'];
  const families: Array<[PathogenFamily, string]> = [
    ['respiratory', 'Resp'], ['haemorrhagic', 'VHF'], ['vector', 'Vector'], ['enteric', 'Enteric'], ['zoonotic', 'Zoo'],
  ];
  return (
    <div style={wrap} role="region" aria-label="Map legend">
      <div style={head} onClick={() => setOpen(o => !o)}>
        <span aria-hidden>{open ? '▾' : '▸'}</span>
        <span>Legend</span>
      </div>
      {open && (
        <>
          <div style={sec}>Colour · threat</div>
          <div style={row}>
            {tiers.map(t => (
              <span key={t} style={item}>
                <span style={dot(THREAT_TIER_META[t].color)} aria-hidden />
                {THREAT_TIER_META[t].label.toLowerCase()}
              </span>
            ))}
          </div>
          <div style={sec}>Shape · family</div>
          <div style={row}>
            {families.map(([f, l]) => (
              <span key={f} style={item}>
                <svg width={12} height={12} viewBox="0 0 24 24" style={{ overflow: 'visible' }} aria-hidden>
                  <g style={{ fill: 'none', stroke: 'var(--bm-text-muted)', strokeWidth: 2 }} dangerouslySetInnerHTML={{ __html: shapeSvg(f) }} />
                </svg>
                {l}
              </span>
            ))}
          </div>
          <div style={sec}>State</div>
          <div style={row}>
            <span style={item}><span style={{ ...dot('var(--bm-prov-confirmed)') }} aria-hidden />confirmed</span>
            <span style={item}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', border: '1.5px dashed var(--bm-prov-unverified)', display: 'inline-block' }} aria-hidden />
              unverified
            </span>
            <span style={item}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', border: '1.5px dashed var(--bm-prov-stale)', display: 'inline-block', opacity: 0.6 }} aria-hidden />
              ⏳ stale
            </span>
          </div>
          <div style={{ marginTop: 8, paddingTop: 6, borderTop: '1px solid var(--bm-border)', fontSize: 10.5, color: 'var(--bm-prov-synthetic)' }}>
            ⬡ dashed ring = genomic detections — <strong>NOT a case count</strong>
          </div>
        </>
      )}
    </div>
  );
}
